/**
 * fog — slow drifting soft haze bands. Big, low-alpha `glow_soft` sprites
 * scattered across the region's lower-middle air band; each self-animates via
 * infinite yoyo tweens (a slow horizontal drift + an alpha breathe on a seeded
 * period) so the haze reads as living mist, not static blobs. Same builder
 * shape as firefliesEffect: narrow by kind, place, drive sprites, return a
 * sprite handle, self-register. Under reduced motion each band holds its spawn
 * x at an alpha midway through the breathe (as god rays do).
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { placeFog } from './fogConfig';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import type { ISprite } from '../../../rendering/IRenderer';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';

const buildFog: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'fog') return null;
  const sprites: ISprite[] = [];
  const reduce = getMotionPrefs().reduceMotion; // read once at build time
  for (const b of placeFog(layer, ctx.region, ctx.seed)) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const s = createGlowSprite({ x: b.x, y: b.y, size: b.size, color: b.color, alpha: b.alphaMin, parent: ctx.layerContainer });
    if (!s) break;
    sprites.push(s);
    if (reduce) {
      s.setAlpha((b.alphaMin + b.alphaMax) / 2);
      continue;
    }
    // Slow horizontal drift around the spawn x (sign baked into driftPx per band).
    s.tween({ x: b.x + b.driftPx }, { durationMs: b.periodMs, ease: 'sineInOut', yoyo: true, repeat: -1 });
    // Alpha breathe between alphaMin..alphaMax on half the drift period (async per band).
    s.tween({ alpha: b.alphaMax }, { durationMs: b.periodMs / 2, ease: 'sineInOut', yoyo: true, repeat: -1 });
  }
  return makeContentHandle({ sprites });
};

registerEffect({ kind: 'fog', build: buildFog, depth: Z_LAYERS.ATMOSPHERE });
